import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import MainComponent from "./components/Main";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import ScrollToTop from "./components/ui/ScrollToTop";
import TrackParcels from "./components/TrackParcel/TrackParcels";
import Service from "./components/AllService/Service";
import Layout from "./Dashboard/Users/Layout";
import ProtectedRoutes from "./utils/ProtectedRoutes";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import Faq from "./pages/Faq";
import Blog from "./pages/Blog";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiesPolicy from "./pages/CookiesPolicy";
import { SettingsProvider } from "./components/SettingsContext";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import NotFound from "./components/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LazyComponents";

// Lazy loaded components
const LazyQuoteForm = React.lazy(() => import("./components/QuoteForm/QuoteForm"));
const LazyLoginPage = React.lazy(() => import("./components/Auth/LoginPage"));
const LazySignUp = React.lazy(() => import("./components/Auth/SignUp"));
const LazyForgotPassword = React.lazy(() => import("./components/Auth/ForgotPassword"));
const LazyQuotationPage = React.lazy(() => import("./Dashboard/Users/Components/QuotationPage"));
const LazyDashboard = React.lazy(() => import("./Dashboard/Users/Pages/Dashboard"));
const LazyGetQuotes = React.lazy(() => import("./Dashboard/Users/Pages/GetQuotes"));
const LazyParcelTracking = React.lazy(() => import("./Dashboard/Users/Pages/ParcelTracking"));
const LazyProfile = React.lazy(() => import("./Dashboard/Users/Pages/Profile"));
const LazyEmailNotifications = React.lazy(() => import("./Dashboard/Users/Pages/EmailNotification"));
const LazyChangePassword = React.lazy(() => import("./Dashboard/Users/Pages/ChangePassword"));
const LazyShipment = React.lazy(() => import("./Dashboard/Users/Pages/Shipment"));
const LazyQuotes = React.lazy(() => import("./Dashboard/Users/Pages/Quotes"));
const LazyScanner = React.lazy(() => import("./components/Scanner/Scanner"));
const LazyGoogleCallback = React.lazy(() => import("./components/Auth/GoogleCallback"));

// Separate component for the app content that needs access to location
function AppContent() {
  const location = useLocation();
  const noHeaderFooterRoutes = [
    "/login",
    "/signup",
    "/forgot-password",
    "/dashboard",
    "/quotation",
    "/scanner",
  ];

  const shouldRenderHeaderFooter = !noHeaderFooterRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  return (
    <div className="App">
      <ScrollToTop />
      <ToastContainer/>
      {shouldRenderHeaderFooter && <Header />}
      <Routes>
        <Route path="/" element={<MainComponent />} />
        <Route path="/quote/:service" element={<Suspense fallback={<LoadingSpinner />}><LazyQuoteForm /></Suspense>} />
        <Route path="/trackparcels" element={<TrackParcels />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/login" element={<Suspense fallback={<LoadingSpinner />}><LazyLoginPage /></Suspense>} />
        <Route path="/signup" element={<Suspense fallback={<LoadingSpinner />}><LazySignUp /></Suspense>} />
        <Route path="/all-services" element={<Service />} />
        <Route path="/forgot-password" element={<Suspense fallback={<LoadingSpinner />}><LazyForgotPassword /></Suspense>} />
        <Route path="/quotation/:quotationId" element={<Suspense fallback={<LoadingSpinner />}><LazyQuotationPage /></Suspense>} />
        <Route path="/scanner" element={<Suspense fallback={<LoadingSpinner />}><LazyScanner /></Suspense>} />
        <Route path="/cookie-policy" element={<CookiesPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/auth/google/callback" element={<Suspense fallback={<LoadingSpinner />}><LazyGoogleCallback /></Suspense>} />


        <Route element={<ProtectedRoutes />}>
          <Route path="/dashboard" element={<Layout />}>
            <Route index element={<Suspense fallback={<LoadingSpinner />}><LazyDashboard /></Suspense>} />
            <Route path="get-quotes" element={<Suspense fallback={<LoadingSpinner />}><LazyGetQuotes /></Suspense>} />
            <Route path="parcel-tracking" element={<Suspense fallback={<LoadingSpinner />}><LazyParcelTracking /></Suspense>} />
            <Route path="profile" element={<Suspense fallback={<LoadingSpinner />}><LazyProfile /></Suspense>} />
            <Route path="email-notification" element={<Suspense fallback={<LoadingSpinner />}><LazyEmailNotifications /></Suspense>} />
            <Route path="change-password" element={<Suspense fallback={<LoadingSpinner />}><LazyChangePassword /></Suspense>} />

            {/* Shipment Routes */}
            <Route path="all-shipments" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="all" /></Suspense>} />
            <Route path="pending-shipments" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="pending" /></Suspense>} />
            <Route path="shipments-in-transit" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="shipments-in-transit" /></Suspense>} />
            <Route path="completed-shipments" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="delivered" /></Suspense>} />
            <Route path="failed-shipments" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="failed" /></Suspense>} />
            <Route path="declined-shipments" element={<Suspense fallback={<LoadingSpinner />}><LazyShipment type="declined" /></Suspense>} />

            {/* Quotation Routes */}

            <Route path="unpaid-quotations" element={<Suspense fallback={<LoadingSpinner />}><LazyQuotes type="unpaid" /></Suspense>} />
            <Route path="paid-quotations" element={<Suspense fallback={<LoadingSpinner />}><LazyQuotes type="paid" /></Suspense>} />
            <Route path="declined-quotations" element={<Suspense fallback={<LoadingSpinner />}><LazyQuotes type="declined" /></Suspense>} />
            <Route path="all-quotations" element={<Suspense fallback={<LoadingSpinner />}><LazyQuotes type="all" /></Suspense>} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      {shouldRenderHeaderFooter && <Footer />}
    </div>
  );
}

// Main App component that provides the Router context
function App() {
  return (
    <HelmetProvider>
      <SettingsProvider>
        <Router>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </Router>
      </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
